/**
 * CalqTool API Client
 * Calls www.calqtool.com/api/* or relative /api when same origin
 */

import type {
  ApproveDrawingProposalMode,
  DrawingAnalysisCache,
  DrawingProposal,
  ProjectContextFields,
  ProjectLibraryDocument,
  ProjectRecord,
  ProjectSystemLine,
  QuoteDocument,
} from '../types/project';
import type { ReviewsCmsPayload } from '../types/cms-reviews';
import type { BlogPostAdmin, BlogPostDetail, BlogPostListItem } from '../types/blog';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

const AUTH_TOKEN_KEY = 'calqtool_auth_token';
const LAST_TENANT_KEY = 'calqtool_last_tenant_id';

export function getStoredLastTenantId(): string | null {
  try {
    const v = localStorage.getItem(LAST_TENANT_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function setStoredLastTenantId(tenantId: string | null): void {
  try {
    if (tenantId?.trim()) localStorage.setItem(LAST_TENANT_KEY, tenantId.trim());
    else localStorage.removeItem(LAST_TENANT_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredAuthToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function authHeaders(): Record<string, string> {
  const t = getStoredAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function apiUrl(path: string): string {
  return path.startsWith('http') ? path : `${API_BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

function messageFromApiErrorPayload(err: Record<string, unknown>): string {
  const nested = err.error;
  if (nested && typeof nested === 'object' && nested !== null && 'message' in nested) {
    return String((nested as { message?: string }).message ?? 'API error');
  }
  if (typeof err.message === 'string') return err.message;
  if (typeof err.error === 'string') return err.error;
  return 'API error';
}

/** Publieke calls zonder Bearer (bootstrap, register bij lege storage) */
async function fetchApiPublic<T>(path: string, body?: unknown, method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'): Promise<T> {
  const url = apiUrl(path);
  const m = method ?? (body ? 'POST' : 'GET');
  const res = await fetch(url, {
    method: m,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body !== undefined && m !== 'GET' ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const o = err as Record<string, unknown>;
    throw new Error(messageFromApiErrorPayload(o));
  }
  return res.json() as Promise<T>;
}

/** Geen credentials: werkt cross-origin (lokale Vite → worker) zonder CORS-cookie-complicaties. */
export async function fetchPublicConfig(): Promise<{ turnstile_site_key: string | null }> {
  const url = apiUrl('/public-config');
  const res = await fetch(url, { method: 'GET' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>));
  }
  return res.json() as Promise<{ turnstile_site_key: string | null }>;
}

async function fetchApi<T>(
  path: string,
  body?: unknown,
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  extraHeaders?: Record<string, string>
): Promise<T> {
  const url = apiUrl(path);
  const m = method ?? (body ? 'POST' : 'GET');
  const res = await fetch(url, {
    method: m,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...extraHeaders },
    credentials: 'include',
    body: body !== undefined && m !== 'GET' ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const o = err as Record<string, unknown>;
    throw new Error(messageFromApiErrorPayload(o));
  }
  return res.json() as Promise<T>;
}

export const api = {
  /** Geen anonieme trial meer — noop (compat voor oude callers). */
  ensureSession: async () => {},

  /** Bootstrap uitgeschakeld op de server; alleen voor handmatige tests. */
  authBootstrap: async (body?: { tenant_id?: string }) => {
    return fetchApiPublic<{
      ok?: boolean;
      token?: string;
      error?: string;
      message?: string;
    }>(`/auth/bootstrap`, body ?? { tenant_id: 'default' }, 'POST');
  },

  authLogin: async (body: { email: string; password: string; tenant_id?: string }) => {
    const tenant_id = body.tenant_id?.trim() || getStoredLastTenantId() || undefined;
    const payload: { email: string; password: string; tenant_id?: string } = {
      email: body.email,
      password: body.password,
    };
    if (tenant_id) payload.tenant_id = tenant_id;
    const data = await fetchApiPublic<{
      ok?: boolean;
      token?: string;
      tenant_id?: string;
      user_id?: string;
      email?: string;
      role?: string;
      older_sessions_revoked?: number;
    }>(`/auth/login`, payload, 'POST');
    if (data.token) {
      setStoredAuthToken(data.token);
      if (data.tenant_id) setStoredLastTenantId(data.tenant_id);
    }
    return data;
  },

  authRegister: async (body: {
    email: string;
    password: string;
    password_confirm?: string;
    organization_name?: string;
    signup_source?: string;
    turnstile_token?: string;
  }) => {
    const data = await fetchApiPublic<{
      ok?: boolean;
      token?: string;
      tenant_id?: string;
      user_id?: string;
      email?: string;
      role?: string;
      older_sessions_revoked?: number;
    }>(`/auth/register`, body, 'POST');
    if (data.token) {
      setStoredAuthToken(data.token);
      if (data.tenant_id) setStoredLastTenantId(data.tenant_id);
    }
    return data;
  },

  authLogout: async () => {
    try {
      await fetchApi(`/auth/logout`, {}, 'POST');
    } finally {
      setStoredAuthToken(null);
      setStoredLastTenantId(null);
    }
  },

  authForgotPassword: (body: { email: string; tenant_id?: string }) => {
    const tenant_id = body.tenant_id?.trim() || getStoredLastTenantId() || undefined;
    return fetchApiPublic<{ ok?: boolean; message?: string }>(
      `/auth/forgot-password`,
      tenant_id ? { ...body, tenant_id } : { email: body.email },
      'POST'
    );
  },

  authResetPassword: (body: { token: string; password: string }) =>
    fetchApiPublic<{ ok?: boolean; message?: string }>(`/auth/reset-password`, body, 'POST'),

  getInvitationPreview: (token: string) =>
    fetchApiPublic<{
      valid: boolean;
      message?: string;
      organization_name?: string;
      email?: string;
    }>(`/invitations/preview?token=${encodeURIComponent(token)}`, undefined, 'GET'),

  authAcceptInvite: async (body: {
    token: string;
    password: string;
    password_confirm: string;
    turnstile_token?: string;
  }) => {
    const data = await fetchApiPublic<{
      ok?: boolean;
      token?: string;
      tenant_id?: string;
      user_id?: string;
      email?: string;
      role?: string;
      older_sessions_revoked?: number;
    }>(`/auth/accept-invite`, body, 'POST');
    if (data.token) {
      setStoredAuthToken(data.token);
      if (data.tenant_id) setStoredLastTenantId(data.tenant_id);
    }
    return data;
  },

  getMeInvitations: () =>
    fetchApi<{
      organization_name: string;
      members: Array<{ user_id: string; email: string; role: string; created_at: string }>;
      pending_invitations: Array<{ invitation_id: string; email: string; created_at: string; expires_at: string }>;
      limits: {
        max_users: number;
        current_users: number;
        pending_invites: number;
        invites_allowed: boolean;
        invite_blocked_reason: string | null;
      };
    }>(`/me/invitations`, undefined, 'GET'),

  createMeInvitation: (body: { email: string }) =>
    fetchApi<{ ok: boolean; invitation_id: string; expires_at: string }>(`/me/invitations`, body, 'POST'),

  revokeMeInvitation: (invitationId: string) =>
    fetchApi<{ ok: boolean }>(`/me/invitations/${encodeURIComponent(invitationId)}`, undefined, 'DELETE'),

  listAdminInvitations: () =>
    fetchApi<{
      invitations: Array<{
        invitation_id: string;
        tenant_id: string;
        email: string;
        created_at: string;
        expires_at: string;
        accepted_at: string | null;
        revoked_at: string | null;
        tenant_name: string;
        invited_by_email: string | null;
      }>;
    }>(`/admin/invitations`, undefined, 'GET'),

  authChangePassword: (body: { current_password: string; new_password: string }) =>
    fetchApi<{ ok?: boolean; message?: string }>(`/auth/change-password`, body, 'POST'),

  patchMeProfile: (body: { display_name?: string }) =>
    fetchApi<{ display_name: string | null; email: string; has_avatar: boolean }>(`/me/profile`, body, 'PATCH'),

  patchMeTenant: (body: { tenant_display_name: string }) =>
    fetchApi<{ tenant_display_name: string }>(`/me/tenant`, body, 'PATCH'),

  uploadMeAvatar: async (file: File) => {
    const url = apiUrl('/me/avatar');
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>));
    }
    return res.json() as Promise<{ ok: boolean; has_avatar: boolean }>;
  },

  deleteMeAvatar: () => fetchApi<{ ok: boolean; has_avatar: boolean }>(`/me/avatar`, undefined, 'DELETE'),

  resolveInput: (body: { text?: string; drawing_url?: string }) =>
    fetchApi<{ resolved: boolean; intent?: unknown }>(`/resolve_input`, body),

  analyzeDocuments: (body: {
    documents: Array<{ type?: 'image' | 'pdf'; url: string; label?: string }>;
    customer_wishes?: string;
    project_context?: {
      project_type?: string;
      building_type?: string;
      region_id?: string;
      postal_code?: string;
      index_year?: string;
      customer_type?: string;
      template_id?: string;
    };
    tenant_id?: string;
    options?: { max_tokens?: number };
  }) =>
    fetchApi<{
      resolved: boolean;
      confidence: number;
      intent: { systems: unknown[]; quantity_estimates: Record<string, number> | null };
      analysis_summary: string | null;
      document_insights: unknown[];
      clarification_questions: string[];
      analysis_depth?: 'standard' | 'deep' | 'full';
      analysis_meta?: {
        ruimtes_inventory_completion_applied?: boolean;
        ruimtes_count_pass1?: number;
        ruimtes_count_final?: number;
        ruimtes_threshold_reference?: number | null;
        inventory_engine_warnings_nl?: string[];
        inventory_conflicts?: Array<{
          doc_index: number;
          ruimtenummer: string;
          ruimte_namen: string[];
          message_nl: string;
        }>;
        systems_inventory_autofill_count?: number;
        systems_inventory_autofill_note_nl?: string | null;
      };
      drawing_extras?: {
        overige_waarneembare_elementen?: string[];
        afkortingen_gezien?: Array<{ code?: string; betekenis_nl?: string }>;
        ruimtes_inventaris?: Array<{
          woning_of_eenheid: string;
          verdieping: string;
          ruimtenummer?: string | null;
          ruimte_naam: string;
          doc_index?: number | null;
          opmerking?: string | null;
          oppervlakte_m2?: number | null;
          wandlengte_totaal_m1?: number | null;
          plafondhoogte_m?: number | null;
          aantal_deuren?: number | null;
          deuren_omschrijving?: string | null;
          aantal_raam_kozijn?: number | null;
          kozijnen_omschrijving?: string | null;
          meetwaarden_bron?: 'tekening' | 'geschat' | null;
        }>;
      };
      audit_id: string;
      rate_limit_remaining: number;
    }>(`/analyze_documents`, body),

  listAssistantThreads: (tenantId = 'default') =>
    fetchApi<{ threads: Array<{ thread_id: string; created_at: string; updated_at: string }> }>(
      `/assistant/threads?tenant_id=${encodeURIComponent(tenantId)}`
    ),

  createAssistantThread: (body?: { tenant_id?: string; source?: string }) =>
    fetchApi<{ thread_id: string; assistant_id: string }>(`/assistant/threads`, body ?? {}),

  sendAssistantMessage: (
    threadId: string,
    body: { content: string; tenant_id?: string; source?: string; context?: { route?: string; project_id?: string; project_name?: string } }
  ) =>
    fetchApi<{ message: string; disclaimer?: string }>(`/assistant/threads/${threadId}/messages`, body),

  getAssistantThread: (threadId: string, tenantId = 'default') =>
    fetchApi<{ messages: Array<{ role: string; content: string }> }>(`/assistant/threads/${threadId}?tenant_id=${encodeURIComponent(tenantId)}`),

  /** Publieke landing-assistent (geen auth; rate limit op server) */
  createPublicAssistantThread: () =>
    fetchApiPublic<{ thread_id: string; assistant_id: string }>(`/assistant/public/threads`, {}, 'POST'),

  sendPublicAssistantMessage: (threadId: string, body: { content: string; page?: string }) =>
    fetchApiPublic<{ message: string; disclaimer?: string }>(
      `/assistant/public/threads/${threadId}/messages`,
      body,
      'POST'
    ),

  getPublicAssistantThread: (threadId: string) =>
    fetchApiPublic<{ messages: Array<{ role: string; content: string }> }>(
      `/assistant/public/threads/${threadId}`,
      undefined,
      'GET'
    ),

  uploadDocument: async (file: File, tenantId = 'default') => {
    const formData = new FormData();
    formData.append('file', file);
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api';
    const url = `${base.replace(/\/$/, '')}/upload_document?tenant_id=${encodeURIComponent(tenantId)}`;
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: { ...authHeaders() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>));
    }
    return res.json() as Promise<{ url: string; key: string; size: number; expires_hours: number }>;
  },

  /** Document achter login openen (GET met sessie). */
  fetchDocumentBlob: async (absoluteUrl: string) => {
    const res = await fetch(absoluteUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { ...authHeaders() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>) || 'Bestand kon niet worden geopend.');
    }
    return res.blob();
  },

  calculateSystem: (body: {
    system_id: string;
    variant_id: string;
    quantity: number;
    tenant_id?: string;
    index_year?: string;
    region_id?: string;
    /** Optioneel: tijdelijke overrides; tenant-standaard komt uit Account → Uurtarieven */
    labor_overrides?: Record<string, number>;
  }) => fetchApi<Record<string, unknown>>(`/calculate_system`, body),

  aggregateProject: (body: {
    project_id: string;
    systems: Array<{ system_id: string; variant_id: string; quantity: number; building_part?: string }>;
    project_type?: string;
    building_type?: string;
    tenant_id?: string;
    index_year?: string;
    region_id?: string;
    waste_factor?: number;
    labor_overrides?: Record<string, number>;
  }) => fetchApi<Record<string, unknown>>(`/aggregate_project`, body),

  generateDocument: (body: {
    project_id: string;
    systems: Array<{ system_id: string; variant_id: string; quantity: number; building_part?: string }>;
    output_type?: string;
    tenant_id?: string;
    project_type?: string;
    building_type?: string;
    index_year?: string;
    region_id?: string;
    vat_rate?: number;
    /** Praktische correctie / meerwerkfactor (default 1.05 in worker) */
    waste_factor?: number;
    labor_overrides?: Record<string, number>;
  }) => fetchApi<QuoteDocument>(`/generate_document`, body),

  generateDocumentPdf: async (body: {
    project_id: string;
    systems: Array<{ system_id: string; variant_id: string; quantity: number; building_part?: string }>;
    output_type?: string;
    tenant_id?: string;
    project_type?: string;
    building_type?: string;
    index_year?: string;
    region_id?: string;
    vat_rate?: number;
    waste_factor?: number;
    labor_overrides?: Record<string, number>;
  }): Promise<Blob> => {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api';
    const url = `${base.replace(/\/$/, '')}/generate_document_pdf`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>) || 'PDF genereren mislukt');
    }
    return res.blob();
  },

  submitFeedback: (body: Record<string, unknown>) =>
    fetchApi<Record<string, unknown>>(`/submit_feedback`, body),

  getPublicAnnouncement: () =>
    fetchApiPublic<{
      announcement: { message: string; severity: string; updated_at: string } | null;
    }>(`/public/announcement`, undefined, 'GET'),

  getAdminAnnouncement: () =>
    fetchApi<{ announcement: { message: string; severity: string; updated_at: string } | null }>(
      `/admin/announcement`,
      undefined,
      'GET'
    ),

  putAdminAnnouncement: (body: { message: string; severity?: string }) =>
    fetchApi<{ ok: boolean; announcement: { message: string; severity: string; updated_at: string } | null }>(
      `/admin/announcement`,
      body,
      'PUT'
    ),

  getPublicCmsReviews: () =>
    fetchApiPublic<{ reviews_cms: ReviewsCmsPayload }>(`/public/cms-reviews`, undefined, 'GET'),

  getAdminCmsReviews: () =>
    fetchApi<{ reviews_cms: ReviewsCmsPayload; stored?: boolean }>(`/admin/cms-reviews`, undefined, 'GET'),

  putAdminCmsReviews: (body: ReviewsCmsPayload) =>
    fetchApi<{ ok: boolean; reviews_cms: ReviewsCmsPayload }>(`/admin/cms-reviews`, body, 'PUT'),

  getPublicBlogPosts: () => fetchApiPublic<{ posts: BlogPostListItem[] }>(`/blog/posts`, undefined, 'GET'),

  getPublicBlogPost: (slug: string) =>
    fetchApiPublic<{ post: BlogPostDetail }>(`/blog/posts/${encodeURIComponent(slug)}`, undefined, 'GET'),

  getAdminBlogPosts: () =>
    fetchApi<{ posts: BlogPostAdmin[] }>(`/admin/blog/posts`, undefined, 'GET'),

  getAdminBlogPost: (slug: string) =>
    fetchApi<{ post: BlogPostAdmin }>(`/admin/blog/posts/${encodeURIComponent(slug)}`, undefined, 'GET'),

  putAdminBlogPost: (
    slug: string,
    body: Partial<
      Pick<
        BlogPostAdmin,
        | 'title'
        | 'excerpt'
        | 'body_html'
        | 'meta_title'
        | 'meta_description'
        | 'keywords'
        | 'category'
        | 'author'
        | 'reading_minutes'
        | 'published'
        | 'published_at'
      >
    >
  ) => fetchApi<{ ok: boolean; slug: string }>(`/admin/blog/posts/${encodeURIComponent(slug)}`, body, 'PUT'),

  postAdminBlogPost: (body: {
    slug: string;
    title: string;
    excerpt: string;
    body_html: string;
    meta_title?: string;
    meta_description?: string;
    keywords?: string;
    category?: string;
    author?: string;
    reading_minutes?: number;
    published?: number;
    published_at?: string;
  }) => fetchApi<{ ok: boolean; slug: string }>(`/admin/blog/posts`, body, 'POST'),

  compareRevisions: (body: { revision_a: unknown; revision_b: unknown }) =>
    fetchApi<Record<string, unknown>>(`/compare_revisions`, body),

  validatePayload: (body: { schema_type: string; payload: unknown }) =>
    fetchApi<{ valid: boolean; errors: string[] }>(`/validate_payload`, body),

  getTemplates: () =>
    fetchApi<Array<{
      template_id: string;
      label: string;
      systems: Array<{ system_id: string; variant_id: string; quantity_default?: number }>;
      project_type?: string;
      building_type?: string;
    }>>(`/templates`),

  createProjectFromTemplate: (body: { template_id: string; project_id?: string; tenant_id?: string }) =>
    fetchApi<{
      project_id: string;
      template_id: string;
      template_label: string;
      project_type?: string;
      building_type?: string;
      systems: Array<{ system_id: string; variant_id: string; quantity: number }>;
    }>(`/create_project_from_template`, body),

  getLibraryGroups: () =>
    fetchApi<{
      groups: Array<{
        group_code: string;
        label_nl: string;
        sort_order: number;
        discipline_hint?: string;
      }>;
    }>(`/library/groups`),

  getLibrarySubgroups: (groupCode: string) =>
    fetchApi<{ group_code: string; subgroups: Array<{ code: string; label_nl: string }> }>(
      `/library/subgroups?group_code=${encodeURIComponent(groupCode)}`
    ),

  getSystems: (params?: {
    project_id?: string;
    project_type?: string;
    building_type?: string;
    group_code?: string;
    q?: string;
    discipline?: string;
    catalog_subgroup?: string;
    tenant_id?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', params.project_id);
    if (params?.project_type) q.set('project_type', params.project_type);
    if (params?.building_type) q.set('building_type', params.building_type);
    if (params?.group_code) q.set('group_code', params.group_code);
    if (params?.q) q.set('q', params.q);
    if (params?.discipline) q.set('discipline', params.discipline);
    if (params?.catalog_subgroup) q.set('catalog_subgroup', params.catalog_subgroup);
    if (params?.tenant_id) q.set('tenant_id', params.tenant_id);
    const qs = q.toString();
    return fetchApi<{
      catalog_version?: string;
      catalog_source?: string;
      systems: Array<{
        system_id: string;
        element_id?: string;
        label: string;
        unit: string;
        discipline?: string;
        discipline_code?: string;
        system_type?: string;
        group_code?: string;
        catalog_subgroup?: string;
        search_keywords?: string[];
      }>;
      library_context?: { project_type: string; building_type: string };
      limit?: number;
      discipline_relaxed?: boolean;
    }>(`/systems${qs ? `?${qs}` : ''}`);
  },
  getCatalogDisciplines: () =>
    fetchApi<{ catalog_version?: string; catalog_source?: string; disciplines: string[] }>(`/catalog/disciplines`),
  /** Samenvatting hele merged library (tenant): aantallen + versie — geen projectcontext nodig */
  getCatalogMeta: (tenantId = 'default') =>
    fetchApi<{
      catalog_version?: string;
      catalog_source?: string;
      snapshot_schema?: string;
      counts?: { systems_active: number; variants_active: number };
    }>(`/catalog/meta?tenant_id=${encodeURIComponent(tenantId)}`),
  /** Referentiematerialen (seed + D1), doorzoekbaar — los van begrotingsregels */
  getCatalogMaterials: (params?: { q?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.q) q.set('q', params.q);
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return fetchApi<{
      catalog_version?: string;
      catalog_source?: string;
      total: number;
      limit: number;
      offset: number;
      materials: Array<{
        material_id: string;
        name: string;
        unit: string;
        base_price: number;
        currency: string;
        index_key: string;
        evidence_grade?: string;
        co2_per_unit?: number;
      }>;
    }>(`/catalog/materials${qs ? `?${qs}` : ''}`);
  },
  getSystemVariants: (systemId: string, tenantId = 'default') =>
    fetchApi<{
      catalog_version?: string;
      catalog_source?: string;
      variants: Array<{ variant_id: string; label: string }>;
    }>(
      `/systems/${encodeURIComponent(systemId)}/variants?tenant_id=${encodeURIComponent(tenantId)}`
    ),
  /** @deprecated Legacy template-benchmark; primair prijsboek via getSystems. */
  getReferenceCosts: (params?: { region_id?: string; building_type?: string; index_year?: string; tenant_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.region_id) q.set('region_id', params.region_id);
    if (params?.building_type) q.set('building_type', params.building_type);
    if (params?.index_year) q.set('index_year', params.index_year);
    if (params?.tenant_id) q.set('tenant_id', params.tenant_id ?? 'default');
    const qs = q.toString();
    return fetchApi<{
      reference_costs: Array<{
        template_id: string;
        label: string;
        project_type: string;
        building_type: string;
        total: number;
        region_id: string;
        index_year: string;
      }>;
      region_label?: string;
    }>(`/reference_costs${qs ? `?${qs}` : ''}`);
  },
  getRegions: () => fetchApi<{ regions: Array<{ region_id: string; label: string; factor: number }> }>(`/regions`),

  /** Heuristiek: NL-postcode → region_id (GET /region_suggest) */
  suggestRegionFromPostalCode: (postalCode: string) => {
    const pc = postalCode.replace(/\s+/g, '').toUpperCase();
    return fetchApi<{ region_id: string; label: string; method: string }>(
      `/region_suggest?postal_code=${encodeURIComponent(pc)}`
    );
  },
  getIndexYears: () => fetchApi<{ index_years: string[] }>(`/index_years`),
  getProjectTypes: () => fetchApi<{ project_types: Array<{ id: string; label: string }> }>(`/project_types`),
  getBuildingTypes: () => fetchApi<{ building_types: Array<{ id: string; label: string }> }>(`/building_types`),

  getAuthMe: () =>
    fetchApi<{
      tenant_id: string;
      user_id: string | null;
      email: string | null;
      role: string | null;
      authenticated?: boolean;
      session_kind?: string | null;
      display_name?: string | null;
      has_avatar?: boolean;
      tenant_display_name?: string | null;
      tenant_billing?: import('../types/billing').TenantBillingPublic;
    }>(`/auth/me`),

  getTenantBilling: (tenantId: string) =>
    fetchApi<{
      tenant_id: string;
      billing_status: string;
      trial_ends_at: string | null;
      usage_exports: number;
      usage_analyses: number;
      billing_plan_label: string | null;
      billing_notes_internal: string | null;
      project_count: number;
    }>(`/tenants/${encodeURIComponent(tenantId)}/billing`),

  putTenantBilling: (
    tenantId: string,
    body: {
      billing_status?: string;
      trial_ends_at?: string | null;
      usage_exports?: number;
      usage_analyses?: number;
      billing_plan_label?: string | null;
      billing_notes_internal?: string | null;
    }
  ) =>
    fetchApi<{
      ok: boolean;
      tenant_id: string;
      billing_status?: string;
      trial_ends_at?: string | null;
      usage_exports?: number;
      usage_analyses?: number;
      billing_plan_label?: string | null;
      billing_notes_internal?: string | null;
      project_count?: number;
    }>(`/tenants/${encodeURIComponent(tenantId)}/billing`, body, 'PUT'),

  billingPortal: (tenantId?: string) => {
    const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    return fetchApi<{
      tenant_id: string;
      stripe_customer_id: string | null;
      plans: Record<string, { id: string; label: string; monthly_ex_vat_cents: number; yearly_ex_vat_cents: number }>;
      subscriptions: Array<{
        subscription_id: string;
        plan_id: string;
        interval: string;
        status: string;
        current_period_start: string | null;
        current_period_end: string | null;
        cancel_at_period_end: number;
        created_at: string;
      }>;
      invoices: Array<{
        invoice_id: string;
        invoice_number: string;
        status: string;
        total_cents: number;
        currency: string;
        due_at: string;
        paid_at: string | null;
        created_at: string;
      }>;
      payment_methods: Array<{
        method_id: string;
        type: string;
        iban_last4: string | null;
        mandate_reference: string | null;
        mandate_signed_at: string | null;
      }>;
      billing_sync_recovered?: boolean;
      billing_sync_reason?: string | null;
    }>(`/billing/portal${q}`);
  },

  billingAbandonCheckout: (body?: { tenant_id?: string }) =>
    fetchApi<{ ok: true; removed: number }>(`/billing/abandon-checkout`, body ?? {}, 'POST'),

  billingCheckout: (body: {
    plan_id: string;
    interval: 'month' | 'year';
    method?: 'stripe';
    legal_name?: string;
    tenant_id?: string;
  }) =>
    fetchApi<{
      subscription_id: string;
      invoice_id: string;
      invoice_number: string;
      total_cents: number;
      currency: string;
      checkout_url?: string;
      transfer_reference?: string;
      transfer_instructions?: string;
    }>(`/billing/checkout`, body, 'POST'),

  billingStripePortal: (body?: { tenant_id?: string }) =>
    fetchApi<{ url: string }>(`/billing/stripe-portal`, body ?? {}, 'POST'),

  billingChangePlan: (body: { plan_id: string; interval: 'month' | 'year'; tenant_id?: string }) =>
    fetchApi<{
      ok: true;
      subscription_id: string;
      plan_id: string;
      interval: string;
      status: string;
    }>(`/billing/change-plan`, body, 'POST'),

  listAdminTenants: () =>
    fetchApi<{
      tenants: Array<{
        tenant_id: string;
        name: string;
        created_at: string;
        billing_status: string;
        trial_ends_at: string;
        billing_plan_label: string;
        user_count: number;
        project_count: number;
        subscription_status: string | null;
        subscription_plan_id: string | null;
      }>;
    }>(`/admin/tenants`, undefined, 'GET'),

  getBillingPlatformMetrics: () =>
    fetchApi<{
      mrr_cents: number;
      mrr_eur: string;
      active_subscriptions_rows: Array<{ plan_id: string; interval: string; c: number }>;
      churned_subscriptions_30d: number;
      new_subscriptions_30d: number;
      open_invoices: number;
      past_due_tenants: number;
      generated_at: string;
    }>(`/billing/platform/metrics`),

  exportBillingPlatformCsv: async () => {
    const url = apiUrl(`/billing/platform/export?format=csv`);
    const res = await fetch(url, {
      method: 'GET',
      headers: { ...authHeaders() },
      credentials: 'include',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>) || 'Export mislukt');
    }
    return res.blob();
  },

  listProjects: (tenantId = 'default', opts?: { limit?: number; offset?: number }) => {
    const q = new URLSearchParams({ tenant_id: tenantId });
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    if (opts?.offset != null) q.set('offset', String(opts.offset));
    return fetchApi<{ projects: unknown[]; limit: number; offset: number }>(`/projects?${q}`);
  },
  createProject: (body: {
    systems: ProjectSystemLine[];
    template_id?: string;
    metadata?: object;
    project_type?: string;
    building_type?: string;
    tenant_id?: string;
    context?: ProjectContextFields;
    drawing_proposal?: Omit<DrawingProposal, 'created_at' | 'approved_at'>;
  }) => fetchApi<ProjectRecord>(`/projects`, body),
  getProject: (projectId: string, tenantId = 'default') =>
    fetchApi<ProjectRecord>(`/projects/${projectId}?tenant_id=${encodeURIComponent(tenantId)}`),
  updateProject: (
    projectId: string,
    body: {
      systems?: ProjectSystemLine[];
      template_id?: string;
      metadata?: object;
      project_type?: string;
      building_type?: string;
      tenant_id?: string;
      context?: Partial<ProjectContextFields>;
      drawing_proposal?: DrawingProposal;
      approve_drawing_proposal?: boolean;
      /** replace (API-standaard) | append | merge */
      approve_drawing_proposal_mode?: ApproveDrawingProposalMode;
      /** Verwijdert concept-tekeningvoorstel volledig */
      clear_drawing_proposal?: boolean;
      library_documents?: ProjectLibraryDocument[];
      drawing_analysis_cache?: DrawingAnalysisCache | null;
      clear_drawing_analysis_cache?: boolean;
    },
    opts?: { revisionSeq?: number }
  ) =>
    fetchApi<ProjectRecord>(
      `/projects/${projectId}`,
      body,
      'PUT',
      opts?.revisionSeq != null ? { 'If-Match': `W/"rev-${opts.revisionSeq}"` } : undefined
    ),
  deleteProject: (projectId: string, tenantId = 'default') =>
    fetchApi<{ deleted: boolean }>(`/projects/${projectId}?tenant_id=${encodeURIComponent(tenantId)}`, undefined, 'DELETE'),

  duplicateProject: (projectId: string, tenantId = 'default') =>
    fetchApi<ProjectRecord>(
      `/projects/${projectId}/duplicate?tenant_id=${encodeURIComponent(tenantId)}`,
      {},
      'POST'
    ),

  wozSupport: (body: { project_id: string; systems: unknown[]; address?: string; woz_reference?: number; index_year?: string; region_id?: string }) =>
    fetchApi<Record<string, unknown>>(`/woz_support`, body),
  calculateSubsidies: (body: { project_id: string; systems: unknown[]; index_year?: string; region_id?: string }) =>
    fetchApi<Record<string, unknown>>(`/calculate_subsidies`, body),
  compareScenarios: (body: {
    project_a: { project_id: string; systems: unknown[]; project_type?: string; building_type?: string };
    project_b: { project_id: string; systems: unknown[]; project_type?: string; building_type?: string };
    index_year?: string;
    region_id?: string;
    tenant_id?: string;
    labor_overrides?: Record<string, number>;
  }) => fetchApi<Record<string, unknown>>(`/compare_scenarios`, body),

  getTenantLaborRates: (tenantId: string) =>
    fetchApi<{
      tenant_id: string;
      profiles: Array<{
        labor_profile_id: string;
        name: string;
        discipline: string;
        default_hourly_rate: number;
        override_hourly_rate: number | null;
        effective_hourly_rate: number;
      }>;
    }>(`/tenants/${encodeURIComponent(tenantId)}/labor-rates`, undefined, 'GET'),

  putTenantLaborRates: (tenantId: string, body: { rates: Record<string, number | null> }) =>
    fetchApi<{
      tenant_id: string;
      profiles: Array<{
        labor_profile_id: string;
        name: string;
        discipline: string;
        default_hourly_rate: number;
        override_hourly_rate: number | null;
        effective_hourly_rate: number;
      }>;
    }>(`/tenants/${encodeURIComponent(tenantId)}/labor-rates`, body, 'PUT'),

  getTenantOverrides: (tenantId: string) =>
    fetchApi<{ overrides: unknown[] }>(`/tenants/${tenantId}/overrides`),
  putTenantOverrides: (tenantId: string, overrides: Array<{ target_type: string; target_id: string; value: number | object }>) =>
    fetchApi<{ overrides: unknown[] }>(`/tenants/${tenantId}/overrides`, { overrides }, 'PUT'),

  listMaintenancePlans: (tenantId = 'default') =>
    fetchApi<{ plans: unknown[] }>(`/maintenance_plans?tenant_id=${encodeURIComponent(tenantId)}`),
  createMaintenancePlan: (body: { building_id?: string; name?: string; years: unknown[]; reserve_total?: number; tenant_id?: string }) =>
    fetchApi<{ plan_id: string }>(`/maintenance_plans`, body),
  getMaintenancePlan: (planId: string, tenantId = 'default') =>
    fetchApi<{ plan_id: string; years: unknown[] }>(`/maintenance_plans/${planId}?tenant_id=${encodeURIComponent(tenantId)}`),

  listPortfolios: (tenantId = 'default') =>
    fetchApi<{ portfolios: unknown[] }>(`/portfolios?tenant_id=${encodeURIComponent(tenantId)}`),
  createPortfolio: (body: { name: string; tenant_id?: string }) =>
    fetchApi<{ portfolio_id: string }>(`/portfolios`, body),
  getPortfolioProjects: (portfolioId: string, tenantId = 'default') =>
    fetchApi<{ portfolio_id: string; projects: unknown[] }>(`/portfolios/${portfolioId}/projects?tenant_id=${encodeURIComponent(tenantId)}`),
  addProjectToPortfolio: (portfolioId: string, body: { project_id: string; tenant_id?: string }) =>
    fetchApi<{ added: boolean }>(`/portfolios/${portfolioId}/projects`, body),

  trackAnalyticsEvent: (body: { event_type: string; path?: string; referrer?: string; session_id?: string; tenant_id?: string }) =>
    fetchApi<{ ok: boolean; event_id: string }>(`/analytics/event`, body),

  /** Admin: dekking per hoofdgroep (merged seed+D1), incl. systemen/varianten per regel */
  getAdminLibraryCoverage: (params?: { tenant_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.tenant_id) q.set('tenant_id', params.tenant_id);
    const qs = q.toString();
    return fetchApi<{
      catalog_version?: string;
      catalog_source?: string;
      catalog_released_at?: string;
      snapshot_schema?: string;
      tenant_id: string;
      generated_at: string;
      groups: Array<{
        group_code: string;
        label_nl: string;
        system_count: number;
        variant_count: number;
        avg_variants_per_system: number;
        max_variants_per_system: number;
        variant_depth_distribution: Array<{ depth: number; system_count: number }>;
        variant_diepte_histogram_nl: string;
        variant_diepte_editorial_nl: string | null;
        beoordeling_editorial_nl: string | null;
        beoordeling_auto_nl: string;
        systems: Array<{
          system_id: string;
          label: string;
          catalog_subgroup: string | null;
          variant_count: number;
          variants: Array<{ variant_id: string; label: string }>;
        }>;
      }>;
    }>(`/admin/library-coverage${qs ? `?${qs}` : ''}`);
  },

  getAnalyticsStats: (params?: { tenant_id?: string; days?: number }) => {
    const q = new URLSearchParams();
    if (params?.tenant_id) q.set('tenant_id', params.tenant_id);
    if (params?.days != null) q.set('days', String(params.days));
    const qs = q.toString();
    return fetchApi<{
      page_views: number;
      project_created: number;
      offerte_generated: number;
      conversion_pct: string;
      events_by_type: Record<string, number>;
      top_pages: Array<{ path: string; count: number }>;
      period_days: number;
      tenant_id: string;
      page_views_last_24h: number;
      unique_sessions_24h: number;
      signup_source_breakdown: Array<{ source_key: string; label: string; count: number }>;
      projects_by_region: Array<{ region_id: string; label: string; count: number }>;
      projects_by_project_type: Array<{ project_type: string; label: string; count: number }>;
      projects_by_building_type: Array<{ building_type: string; label: string; count: number }>;
      tool_woz_support: number;
      tool_subsidies: number;
      tool_compare_scenarios: number;
    }>(`/analytics/stats${qs ? `?${qs}` : ''}`);
  },

  /** Admin: CSV-export (UTF-8 BOM, standaard ; als scheidingsteken). */
  /** Superadmin: SEO-dashboard payload (traffic-proxy, robots/sitemap, URL-probes). */
  getAdminSeoHealth: (params?: { days?: number; site_origin?: string; max_url_checks?: number }) => {
    const q = new URLSearchParams();
    if (params?.days != null) q.set('days', String(params.days));
    if (params?.site_origin) q.set('site_origin', params.site_origin);
    if (params?.max_url_checks != null) q.set('max_url_checks', String(params.max_url_checks));
    const qs = q.toString();
    return fetchApi<{
      generated_at: string;
      site_origin: string;
      period_days: number;
      site_origin_source: string;
      traffic: {
        page_views_total: number;
        unique_sessions_estimate: number;
        channel_breakdown: Array<{ channel: string; label_nl: string; count: number }>;
        top_landing_from_search_referrer: Array<{ path: string; count: number }>;
        top_marketing_paths: Array<{ path: string; count: number }>;
        top_referrers: Array<{ referrer: string; count: number }>;
      };
      search_performance_proxy: {
        disclaimer_nl: string;
        rows: Array<{ path: string; clicks_proxy: number; note: string }>;
      };
      indexing_and_crawl: {
        disclaimer_nl: string;
        robots: {
          url: string;
          ok: boolean;
          status: number;
          snippet: string | null;
          mentions_sitemap: boolean;
        };
        sitemaps: Array<{
          url: string;
          ok: boolean;
          status: number;
          url_count_in_file: number;
          is_index: boolean;
        }>;
        url_samples: Array<{
          url: string;
          status: number;
          ok: boolean;
          final_url?: string;
          content_type?: string | null;
        }>;
        homepage_signals: {
          url: string;
          ok: boolean;
          has_title: boolean;
          has_meta_description: boolean;
          title_preview: string | null;
        };
      };
      external: {
        google_search_console_url: string;
        bing_webmaster_url: string;
        note_nl: string;
      };
    }>(`/admin/seo-health${qs ? `?${qs}` : ''}`);
  },

  /** Superadmin: programmatic SEO pages.json + keyword-catalog van de live site. */
  getAdminSeoPages: (params?: { site_origin?: string }) => {
    const q = new URLSearchParams();
    if (params?.site_origin) q.set('site_origin', params.site_origin);
    const qs = q.toString();
    return fetchApi<{
      generated_at: string;
      site_origin: string;
      site_origin_source: string;
      pages_json_url: string;
      keyword_catalog_url: string;
      pages_fetch_ok: boolean;
      pages_http_status: number;
      pages: unknown;
      page_count: number;
      keyword_catalog_fetch_ok: boolean;
      keyword_catalog_http_status: number;
      keyword_catalog: unknown;
    }>(`/admin/seo-pages${qs ? `?${qs}` : ''}`);
  },

  exportAnalyticsStatsCsv: async (params?: { tenant_id?: string; days?: number; delimiter?: ';' | ',' }) => {
    const q = new URLSearchParams();
    if (params?.tenant_id) q.set('tenant_id', params.tenant_id);
    if (params?.days != null) q.set('days', String(params.days));
    q.set('format', 'csv');
    if (params?.delimiter) q.set('delimiter', params.delimiter);
    const url = apiUrl(`/analytics/stats?${q.toString()}`);
    const res = await fetch(url, {
      method: 'GET',
      headers: { ...authHeaders() },
      credentials: 'include',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>) || 'Export mislukt');
    }
    return res.blob();
  },

  exportProject: async (
    body: {
      project_id: string;
      systems: Array<{ system_id: string; variant_id: string; quantity: number; building_part?: string }>;
      format?: 'json' | 'csv' | 'excel' | 'cuf' | 'pdf_text' | 'pdf';
      tenant_id?: string;
      project_type?: string;
      building_type?: string;
      index_year?: string;
      region_id?: string;
      vat_rate?: number;
      waste_factor?: number;
      /** Standaard ; (NL-Excel). Komma voor internationale tools. */
      csv_delimiter?: ';' | ',';
      /** Standaard true — juridische voorbehoudregel onderaan CSV */
      include_export_disclaimer?: boolean;
      /** Standaard true — blok “per deel” in begroting-PDF / pdf_text */
      include_per_deel_begroting?: boolean;
      /** Standaard true — kostenopbouw (mat/arb/matr, subtotaal, modelcorrectie) bovenaan CSV/Excel/PDF */
      include_cost_summary?: boolean;
      /** Standaard false — extra kolommen Soort, Bouwdeel, Systeem in CSV/Excel en tabel in begroting-PDF */
      include_regel_detail?: boolean;
      /** Standaard false — CO₂-regel in samenvatting indien beschikbaar */
      include_co2?: boolean;
      /** Optioneel — titelregel op begroting-PDF */
      project_label?: string;
      labor_overrides?: Record<string, number>;
    }
  ): Promise<Blob> => {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api';
    const url = `${base.replace(/\/$/, '')}/export_project`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify({ ...body, format: body.format ?? 'csv' }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(messageFromApiErrorPayload(err as Record<string, unknown>) || 'Export mislukt');
    }
    return res.blob();
  },
};
